import { trackEvent, trackError } from '@/helpers/tracking.helpers';
import { TrackingEvents } from './enum';

// Need to move it to Environment variables later on.
export const IMAGE_BASE_URLS = {
    STOCK_LOGO_NEW: 'https://media.traderverse.io/assets/equity/',
    CRYPTO_LOGO_NEW: 'https://media.traderverse.io/assets/crypto/',
    STOCK_LOGO_OLD: 'https://media.traderverse.io/assets/equity/',
    CRYPTO_LOGO_OLD: 'https://media.traderverse.io/assets/crypto/',
    FALLBACK_LOGO: 'https://cdn-images.traderverse.io/',
};

const EQUITY_EXCHANGES = new Set([
    'NASDAQ', 'NYSE', 'AMEX', 'XNGS', 'ARCA', 'BATS', 'OTC', 'CBOE', 'IEX',
]);

/** Build media asset id, e.g. AAPL_NASDAQ or BTC_CRYPTO */
export const resolveMediaAssetId = (
    symbolOrStockId: string,
    market?: string
): { assetId: string; isCrypto: boolean } => {
    const raw = String(symbolOrStockId || '').trim();
    const marketLower = String(market || '').toLowerCase();
    const upper = raw.toUpperCase().replace(/\s+/g, '');

    const looksCrypto =
        marketLower === 'crypto' ||
        upper.endsWith('_CRYPTO') ||
        /-USD$/i.test(upper);

    if (looksCrypto) {
        let symbol = upper
            .replace(/_CRYPTO$/i, '')
            .replace(/-USD$/i, '')
            .split('_')[0]
            .split('-')[0];
        if (!symbol) symbol = upper || 'UNKNOWN';
        return { assetId: `${symbol}_CRYPTO`, isCrypto: true };
    }

    if (upper.includes('_')) {
        const [, exchange] = upper.split('_');
        if (exchange && EQUITY_EXCHANGES.has(exchange)) {
            return { assetId: upper, isCrypto: false };
        }
        // e.g. BRK_B_NYSE → keep as-is when last segment is exchange
        const parts = upper.split('_');
        const last = parts[parts.length - 1];
        if (EQUITY_EXCHANGES.has(last)) {
            return { assetId: upper, isCrypto: false };
        }
    }

    const symbol = upper.split('_')[0].split('-')[0] || upper;
    return { assetId: `${symbol}_NASDAQ`, isCrypto: false };
};

export const EVENT_IMAGES_S3_BASE_URL = 'https://tv-event.s3.us-east-2.amazonaws.com/'

const isLoggingEnabled = false; // Set to false in production

// Helper function for conditional logging
export function log(message: string, data?: any) {
  if (isLoggingEnabled) {
    console.log(`[ImageLoader] ${message}`, data || '');
  }
}

// Cache for successfully loaded images
const imageCache = new Map<string, string>();
let currentActiveRequests = new Map<string, Promise<ImageLoadResult>>();

export interface ImageLoadOptions {
    src: string;
    altSrc: string;
    element?: HTMLImageElement;
    userId?: string;
    context?: string;
    maxRetries?: number;
    retryDelay?: number;
    ignoreCache?: boolean; // New option to bypass cache
    persistOnError?: boolean; // New option to keep previous image on failure
}

export interface ImageLoadResult {
    success: boolean;
    loadedSrc: string;
    error?: Error;
    retries?: number;
    wasCached?: boolean;

}

export const loadImageWithTracking = async (options: ImageLoadOptions): Promise<ImageLoadResult> => {
    const {
        src,
        altSrc,
        element,
        userId,
        context = 'profile-image',
        maxRetries = 3,
        retryDelay = 300,
        persistOnError = true,
        ignoreCache = false
    } = options;

    log('Starting image load', { src, altSrc, context });

    // Check cache first
    if (imageCache.has(src) && !ignoreCache) {
        log('Returning cached image', { src });
        return {
            success: true,
            loadedSrc: imageCache.get(src)!,
            wasCached: true,
            retries: 0
        };
    }

    // Check if this request is already in progress
    if (currentActiveRequests.has(src)) {
        log('Request already in progress', { src });
        return currentActiveRequests.get(src)!;
    }

    let retries = 0;
    let lastError: Error | null = null;

    const requestPromise = (async () => {
        log('Creating new image load promise', { src });

        const trackImageLoad = (success: boolean, loadedSrc: string, retriesUsed: number) => {
            log('Tracking image load', { success, loadedSrc, retriesUsed });
            trackEvent(success ? TrackingEvents.imageLoadSuccess : TrackingEvents.imageLoadError, {
                src,
                loadedSrc,
                retries: retriesUsed,
                userId,
                context
            });

            if (!success && lastError) {
                log('Tracking image load error', { error: lastError });
                trackError(lastError, {
                    context: 'ImageLoading',
                    src,
                    loadedSrc,
                    retries: retriesUsed,
                    userId
                });
            }
        };

        const attemptLoad = (url: string): Promise<ImageLoadResult> => {
            return new Promise((resolve) => {
                const img = element || new Image();
                log('Attempting to load image', { url });

                img.onload = () => {
                    log('Image loaded successfully', { url });
                    if (!imageCache.has(url)&&!ignoreCache) {
                        imageCache.set(url, url);
                        log('Added to cache', { url });
                    }
                    resolve({
                        success: true,
                        loadedSrc: url
                    });
                };

                img.onerror = (err) => {
                    lastError = new Error(`Failed to load image: ${url}`);
                    log('Image load failed', { url, error: lastError });
                    resolve({
                        success: false,
                        loadedSrc: url,
                        error: lastError
                    });
                };

                img.src = url;
            });
        };

        // Try primary source first
        while (retries <= maxRetries) {
            log('Attempting load (primary)', { src, retry: retries });
            const result = await attemptLoad(src);
            if (result.success) {
                trackImageLoad(true, src, retries);
                return { ...result, retries };
            }

            retries++;
            if (retries <= maxRetries) {
                log('Retrying after delay', { src, retry: retries, delay: retryDelay });
                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
        }

        // If primary fails, try alternate source
        if (altSrc) {
            log('Attempting fallback load (secondary)', { altSrc });
            const altResult = await attemptLoad(altSrc);
            trackImageLoad(altResult.success, altSrc, retries);

            if (altResult.success) {
                return {
                    ...altResult,
                    retries,
                    loadedSrc: altSrc
                };
            }
        }

        log('All load attempts failed', { src, altSrc });
        return {
            success: false,
            loadedSrc: persistOnError ? (element?.src || '') : '',
            error: lastError,
            retries
        };
    })();

    currentActiveRequests.set(src, requestPromise);
    try {
        const result = await requestPromise;
        log('Request completed', { src, result });
        return result;
    } finally {
        currentActiveRequests.delete(src);
        log('Request cleaned up', { src });
    }
};
// Helper function to generate dynamic URLs
// Prefer stockId when available so exchange suffix is preserved (AAPL_NASDAQ, BTC_CRYPTO).
export const generateStockImageUrl = (
    symbol: string,
    market: string,
    stockId?: string
): string => {
    const { assetId, isCrypto } = resolveMediaAssetId(
        stockId || symbol,
        market
    );
    if (isCrypto || String(market || '').toLowerCase() === 'crypto') {
        return `${IMAGE_BASE_URLS.CRYPTO_LOGO_NEW}${assetId}.svg`;
    }
    return `${IMAGE_BASE_URLS.STOCK_LOGO_NEW}${assetId}.svg`;
};

// Helper function to generate fallback URLs
export const generateStockImageUrlFallback = (
    symbol: string,
    market: string,
    stockId?: string
): string => {
    return generateStockImageUrl(symbol, market, stockId);
};

export const generateFallbackImageUrl = (market: string): string => {
    return `${IMAGE_BASE_URLS.FALLBACK_LOGO}${String(market || '').toLowerCase() === 'crypto' ? 'crypto_dummy' : 'stocks_dummy'}.svg`;
};

// Clear cache and active requests
export const clearImageCache = (): void => {
    imageCache.clear();
    currentActiveRequests.clear();
};

// Preload an image and cache it
export const preloadImage = async (url: string): Promise<boolean> => {
    if (imageCache.has(url)) return true;

    const result = await loadImageWithTracking({
        src: url,
        altSrc: '',
        persistOnError: false
    });

    return result.success;
};

// Enhanced function to load stock images with new/old URL fallback logic
export const loadStockImageWithFallback = async (
    symbol: string, 
    market: string, 
    options: Omit<ImageLoadOptions, 'src' | 'altSrc'> = {}
): Promise<ImageLoadResult> => {
    const newUrl = generateStockImageUrl(symbol, market);
    const fallbackUrl = generateStockImageUrlFallback(symbol, market);
    
    log('Loading stock image with fallback', { symbol, market, newUrl, fallbackUrl });
    
    return await loadImageWithTracking({
        src: newUrl,
        altSrc: fallbackUrl,
        ...options
    });
};